import React, { Fragment, useMemo, useState } from "react";
import Pagination from "./Pagination";
import { getCache } from "../../../lib/cache/cache";
import { CacheHookArgs, useCache } from "../../../lib/cache/hooks/useCache";

interface Props<T> {
    data: T[];
    children: (dataToDisplay: T[]) => React.ReactNode;
    searchTerm?: string; // This is purely used for passing to the Pagination Component to reset the page on search change.

    pagination?: boolean;
    perPage?: number;

    cache?: CacheHookArgs["cache"];
    cacheKey?: CacheHookArgs["cacheKey"];
}

/**
 * This component allows us to paginate data and display pagination controls to the user
 * @param param0
 * @returns
 */
const PaginateComponent = <T extends GenericObject>({
    data,
    children,
    searchTerm,

    pagination,
    perPage = 10,

    cache,
    cacheKey,
}: Props<T>) => {
    const [currentPage, setCurrentPage] = useState(cache && getCache(cacheKey) ? getCache(cacheKey) : 0);

    /**
     * We cache the search term based upon the array of objects passed in.
     * This means if we change site and the visit search isnt there anymore then we dont get it.
     */
    useCache({
        cache,
        cacheKey,
        value: currentPage,
    });

    const dataToDisplay = useMemo(() => {
        if (!pagination) {
            return data;
        }

        const start = currentPage * perPage;
        const end = start + perPage;

        return data.slice(start, end);
    }, [currentPage, pagination, perPage, data]);

    // If the search term changes, we need to reset the page to 0
    const searchTermRef = React.useRef(searchTerm);
    React.useEffect(() => {
        if (searchTermRef.current !== searchTerm) {
            setCurrentPage(0);
        }
        searchTermRef.current = searchTerm;
    }, [searchTerm]);

    return (
        <Fragment>
            {children(dataToDisplay)}

            {!!pagination && (
                <div className="flex justify-center">
                    <Pagination
                        itemsPerPage={perPage}
                        itemCount={data.length}
                        onPageChange={setCurrentPage}
                        cacheKey={cacheKey}
                        currentPage={currentPage}
                    />
                </div>
            )}
        </Fragment>
    );
};

export default PaginateComponent;
